/**
 * Curated content for the 6 Life Journeys — plain-language, South-Africa
 * specific, and grounded in real regulatory context (NCA, FAIS, FSCA, TCF).
 *
 * Each step: title · body · checklist[] · questions[] (seeded prompts a
 * consumer can send straight to NAVI · Ask NAVI).
 *
 * Language rules (enforced in the copy):
 *   - Explain, don't advise.
 *   - No product recommendations by brand.
 *   - Always cite the actual right the consumer has (cooling-off, NCA S.129, etc).
 *   - Every dangerous step includes a "what to check" list.
 */

export const JOURNEY_CONTENT = {
  first_home: {
    intro:
      "Buying your first home is the single biggest financial commitment most South Africans ever make. This walk-through covers the pieces most first-time buyers miss — not the sales pitch.",
    steps: [
      {
        title: "Work out what you can actually afford",
        body:
          "Bond repayments should stay under about 30% of your take-home pay — banks may pre-approve you for more, but that leaves nothing for rates, levies, insurance and life. Add roughly 8–12% of the price for once-off costs (transfer duty above R1.1m, bond registration, attorney fees, initiation).",
        checklist: [
          "Calculate 30% of your net (after-tax) monthly income — that's your comfortable bond cap.",
          "Add up existing debt commitments — the NCA affordability check will count them all.",
          "Get a free credit report from at least one bureau (TransUnion, Experian, XDS or Compuscan) and fix errors before you apply.",
        ],
        questions: [
          "How does the transfer duty work on a R1.5m property?",
          "What does a bank actually check in an NCA affordability assessment?",
        ],
      },
      {
        title: "Understand the true cost — not just the bond",
        body:
          "The bond repayment is only about 70% of what a home costs. Rates & taxes, sectional-title levies, insurance, maintenance, and utilities easily add R3 000–R8 000/month. If you're borrowing 100% you'll also pay more in interest — usually 0.5–1% higher rate.",
        checklist: [
          "Ask the estate agent for the last three months' municipal statements.",
          "Ask for the body-corporate financials if it's sectional title (special levies pending?).",
          "Budget 1% of the property value per year for maintenance.",
        ],
        questions: [
          "What's the difference between rates and levies?",
          "Why do 100% bonds cost more?",
        ],
      },
      {
        title: "Get pre-approval before you go house-hunting",
        body:
          "A pre-approval (not a pre-qualification) is a formal offer from a bank — usually valid 90 days. It gives you a real budget, and it makes sellers take your offer seriously. Apply at multiple banks; the offers differ by 0.3–0.75% in rate.",
        checklist: [
          "Apply to at least three banks — the rate difference over 20 years can exceed R400k.",
          "Consider using a bond originator (they don't cost the buyer — the bank pays them).",
          "Keep your credit profile clean during the process — no new credit applications.",
        ],
        questions: [
          "Is a bond originator on my side or the bank's?",
          "What's a 'prime minus' rate and how does it work?",
        ],
      },
      {
        title: "Read the Offer to Purchase (OTP) before you sign",
        body:
          "The OTP is a legally binding contract the moment you sign. Look for the suspensive conditions — clauses that let you walk away if the bond isn't granted, if the property fails inspection, or if the seller can't clear a mortgage. Missing these is how first-time buyers get trapped.",
        checklist: [
          "Confirm there's a bond-approval suspensive clause with realistic dates (usually 21–30 days).",
          "Check the deposit terms and where it will be held (a trust account — not the agent's personal account).",
          "Verify the occupation date and daily occupational rental if you take occupation before transfer.",
        ],
        questions: [
          "What does 'voetstoots' mean and does it still apply under the Consumer Protection Act?",
          "Can I walk away if the bank rejects my bond application?",
        ],
      },
      {
        title: "Insurance — what's compulsory vs oversold",
        body:
          "The bank REQUIRES homeowners' cover (buildings insurance) — but they can't force you to buy it from them. Life cover to service the bond is common practice but NOT compulsory. Bond insurance and credit-life sold at bond registration is where most first-time buyers pay double for cover they already have.",
        checklist: [
          "Get at least two independent homeowners' quotes — the bank's option is often 20–30% more expensive.",
          "Check your existing life cover before agreeing to any new bond-linked policy.",
          "Ask specifically: 'Do I have to buy this to get the bond?' — the honest answer is usually no.",
        ],
        questions: [
          "What's the difference between buildings insurance and homeowners' insurance?",
          "Can the bank refuse my bond if I insure elsewhere?",
        ],
      },
      {
        title: "Transfer & registration — the last 6 weeks",
        body:
          "From accepted offer to keys is usually 8–12 weeks. The transferring attorney (chosen by the seller) handles the deed transfer; the bond attorney (chosen by the bank) registers the bond. You pay both. Ask for a written cost breakdown before you sign anything at the attorney's office.",
        checklist: [
          "Request the pro-forma statement of costs from the transferring attorney upfront.",
          "Confirm the bond registration invoice separately — combined statements can hide fees.",
          "Get your keys only after the deed is registered at the Deeds Office (not before).",
        ],
        questions: [
          "Why do I pay both attorneys, and can I use my own?",
          "What happens if the seller can't move out on the occupation date?",
        ],
      },
    ],
  },

  family: {
    intro:
      "A new child changes your financial picture more than any other single life event. This is what actually needs sorting — before you get sold anything.",
    steps: [
      {
        title: "Medical aid — before baby arrives, not after",
        body:
          "Most schemes have a 12-month waiting period for pregnancy if you join while already pregnant. Add the baby within 30 days of birth to avoid late-joiner penalties and waiting periods. A hospital plan may be enough — full comprehensive cover often isn't necessary until later.",
        checklist: [
          "Register the baby within 30 days of birth — every scheme requires this.",
          "Compare in-hospital benefits, not day-to-day, if budget is tight.",
          "Check the private-hospital birth co-payment — R5–15k is normal even with cover.",
        ],
        questions: [
          "What's the difference between a hospital plan and comprehensive medical aid?",
          "What happens if I move schemes while pregnant?",
        ],
      },
      {
        title: "Life cover — how much is actually enough",
        body:
          "The point of life cover is to replace your income if you die. A common rule: 10–15× your annual income (net of tax). Buy term life, not whole life, unless there's a specific tax reason otherwise — term is 4–8× cheaper for the same cover.",
        checklist: [
          "Add up outstanding bond + a lump sum for children's education + roughly 10 years of your net income.",
          "Get quotes from at least three insurers — premiums for identical cover vary by 40%+.",
          "Nominate a beneficiary immediately — otherwise the payout falls into your deceased estate and is delayed by up to a year.",
        ],
        questions: [
          "Why is term life so much cheaper than whole life?",
          "Should the bond life cover be separate from family life cover?",
        ],
      },
      {
        title: "A Will — legally the most urgent thing",
        body:
          "If you die without a Will (intestate) and have minor children, the state decides who administers their inheritance and who becomes their guardian. A simple Will fixes both. It also takes about R500–R2 000 at any bank or attorney.",
        checklist: [
          "Nominate a guardian for minor children — and check they've agreed.",
          "Set up a testamentary trust to hold assets for minors until 18 or 21.",
          "Store the Will with a bank or attorney and tell your spouse where it is.",
        ],
        questions: [
          "What actually happens if I die without a Will and I have a minor child?",
          "What is a testamentary trust and why would I need one?",
        ],
      },
      {
        title: "Education — start now, even if it's R500 a month",
        body:
          "Education inflation runs 8–10% a year — significantly above general inflation. A R500/month contribution from birth to 18 in a low-cost equity vehicle typically compounds to enough for tertiary. The tax-free savings account (TFSA) is the simplest place to start.",
        checklist: [
          "Open a Tax-Free Savings Account in the child's name if possible (R36 000/year limit as of 2024, lifetime R500k).",
          "Choose a low-cost equity-tracker fund (total expense ratio under 0.5%).",
          "Ignore education-branded endowment 'plans' — they typically add fees, not returns.",
        ],
        questions: [
          "TFSA vs endowment vs unit trust — which is better for education?",
          "What's a Total Expense Ratio (TER) and why does it matter?",
        ],
      },
      {
        title: "Update every existing policy",
        body:
          "Baby is a life event that changes beneficiary nominations, medical-aid dependants, retirement-fund allocations, and often the sum-assured you need. Miss this and life cover payouts can go to the wrong person years later.",
        checklist: [
          "Update beneficiaries on: life policies, retirement annuity, provident/pension fund, group life through your employer.",
          "Update medical-aid dependants list.",
          "Update your Will if there wasn't one before.",
        ],
        questions: [
          "Who inherits my retirement fund if I don't nominate a beneficiary?",
          "How often should I review my beneficiary nominations?",
        ],
      },
    ],
  },

  retirement: {
    intro:
      "Retirement planning in SA has three big traps: starting too late, paying too much in fees, and not knowing what a compulsory annuity actually looks like. Let's take them one at a time.",
    steps: [
      {
        title: "Work out what 'enough' actually is",
        body:
          "A rough SA benchmark: 15–17× your final annual expenses, invested in a diversified pot at retirement, generating a 4–5% annual draw. That's about R6–7m at today's rates for a household spending R30 000/month in retirement.",
        checklist: [
          "Calculate your current monthly essential expenses (bond, utilities, food, medical aid, transport).",
          "Multiply by 12 to get annual expenses.",
          "Multiply annual expenses by 15 for a rough retirement target.",
        ],
        questions: [
          "What's a safe withdrawal rate in SA and how does it compare to the US 4% rule?",
          "Should I include my house in my retirement number?",
        ],
      },
      {
        title: "Understand the vehicles — RA, pension fund, TFSA",
        body:
          "Retirement Annuity (RA), employer pension/provident fund, and Tax-Free Savings Account are the three pillars. Contributions to RA/pension are tax-deductible up to 27.5% of income (capped R350 000/year). TFSA is post-tax but grows and withdraws completely tax-free.",
        checklist: [
          "If your employer offers a pension/provident fund match, contribute enough to get the full match — it's free money.",
          "Maximise the RA tax deduction each year if you have surplus income.",
          "Use the TFSA for anything above the RA limit — R36k/year (2024).",
        ],
        questions: [
          "What's the difference between a pension fund and a provident fund after the 2021 changes?",
          "Should I choose a Reg 28 or non-Reg-28 fund?",
        ],
      },
      {
        title: "Fees — the silent killer of retirement",
        body:
          "A 1% higher fee, compounded over 30 years, can reduce your final pot by 20–30%. Ask for the 'Total Investment Charge' (TIC), broken down into advice, admin, platform and fund costs. Anything above 1.5% p.a. total is expensive by 2024 SA standards.",
        checklist: [
          "Ask specifically for the TIC (Total Investment Charge) as a percentage — not just the fund's TER.",
          "Confirm any advice fees (initial + ongoing) as a percentage and in Rands.",
          "Check for early-termination penalties — some old policies charge if you switch inside 5 years.",
        ],
        questions: [
          "How much money does 1% extra in fees actually cost over 30 years?",
          "Are advice fees always necessary or can I DIY?",
        ],
      },
      {
        title: "Rebalancing — set it and forget it",
        body:
          "Most SA retirement funds are 'Regulation 28 compliant' meaning ≤75% equity, ≤45% offshore. A reasonable default at age 30 is 70/30 equity/bonds; slow-shift to 40/60 by 60. Don't try to time the market — the biggest returns come from being IN it.",
        checklist: [
          "Set an age-appropriate asset allocation and rebalance annually.",
          "Ignore market noise; contribute automatically each month (rand-cost averaging).",
          "Only switch funds for a real reason (fees dropped elsewhere, life change) — not because of a scary headline.",
        ],
        questions: [
          "What is Regulation 28 and why does it exist?",
          "Should I switch funds if the market crashes?",
        ],
      },
      {
        title: "At retirement — the two-thirds rule",
        body:
          "At 55 (or later), you can take up to one-third of your RA/pension as a tax-free lump sum (up to certain thresholds) and MUST use the other two-thirds to buy a compulsory annuity. This is where product choice matters most — living annuity vs guaranteed life annuity.",
        checklist: [
          "Understand the difference between a living annuity (you control, but you can outlive it) and a life annuity (guaranteed for life, but no inheritance).",
          "Get formal quotes from at least three annuity providers.",
          "Model the drawdown rate — 4–5% is sustainable; 8%+ almost always runs out.",
        ],
        questions: [
          "Living annuity vs life annuity — which is better for me?",
          "What's the maximum tax-free lump sum I can take?",
        ],
      },
      {
        title: "Beneficiary nominations on the retirement fund",
        body:
          "Retirement fund payouts on death go through Section 37C of the Pension Funds Act — the trustees decide, not your Will. But they must consider your nominations. Missing this can cost your family 6–18 months of delay.",
        checklist: [
          "Nominate all financial dependants (spouse, children, ageing parents you support).",
          "Update after any life event (marriage, divorce, new child, death of a beneficiary).",
          "Keep a copy of the nomination form somewhere your spouse can find it.",
        ],
        questions: [
          "What is Section 37C and why can the trustees override my nomination?",
          "What happens if I nominate an adult child but I also support my mother?",
        ],
      },
      {
        title: "Estate planning around retirement",
        body:
          "Estate duty kicks in at R3.5m (as of 2024) at 20% (higher rate for larger estates). Retirement funds don't form part of your estate for estate-duty purposes but they DO count for spousal roll-over. Simple estate planning can save your family hundreds of thousands.",
        checklist: [
          "Update your Will to reflect your retirement realities.",
          "Consider whether a trust makes sense (usually only above R5–10m in assets).",
          "Ensure liquidity — enough cash so your family doesn't have to sell your house to pay estate costs.",
        ],
        questions: [
          "How much can I leave without estate duty being triggered?",
          "Do I need a trust or is a Will enough?",
        ],
      },
    ],
  },

  bereavement: {
    intro:
      "In the days after a death there's paperwork you can't skip and paperwork you can safely delay. Here's the calm order that actually works.",
    steps: [
      {
        title: "First 7 days — the essentials only",
        body:
          "Get multiple certified copies of the death certificate — you'll need 6–10 of them. Notify the person's employer, funeral cover provider, and medical aid. Nothing else has to happen this week.",
        checklist: [
          "Order at least 6 certified copies of the death certificate from Home Affairs.",
          "Notify the employer — most workplaces have a group-life policy that pays out quickly.",
          "File the funeral policy claim — payout is usually within 48 hours for valid claims.",
        ],
        questions: [
          "How do I get certified copies of a death certificate?",
          "What's the difference between funeral cover and life insurance?",
        ],
      },
      {
        title: "Weeks 2–4 — appoint an executor",
        body:
          "The Master of the High Court appoints an executor named in the Will (or a family member if there's no Will). The executor is legally responsible for the estate. If the estate is under R250 000, a Section 18(3) process is available — much faster than a full estate.",
        checklist: [
          "Locate the original Will (bank vault, attorney's office, home safe).",
          "Contact the executor named in the Will.",
          "Report the estate to the Master's Office within 14 days of death.",
        ],
        questions: [
          "What does an executor actually do?",
          "How does the Section 18(3) small-estate process work?",
        ],
      },
      {
        title: "Life policies and retirement funds",
        body:
          "Life insurance pays directly to nominated beneficiaries — outside the estate — usually within 30 days. Retirement funds are different: trustees must investigate under S37C of the Pension Funds Act, which can take 6–18 months.",
        checklist: [
          "Locate every life policy, retirement annuity, provident fund, pension fund.",
          "Submit claim forms with the certified death certificate to each provider.",
          "Do NOT wait for retirement fund payouts to fund immediate needs — use life policies first.",
        ],
        questions: [
          "Why does the retirement fund payout take so long?",
          "Can I claim my late spouse's group life if they died at work?",
        ],
      },
      {
        title: "Bank accounts and debts",
        body:
          "The bank freezes accounts on notification of death — including joint accounts. The executor unfreezes them (weeks, not days). Debts don't automatically die — they're paid from the estate. If the estate is insolvent, secured debts get the asset back; unsecured creditors get what's left.",
        checklist: [
          "Notify each bank of the death (they freeze accounts anyway once they see the death record).",
          "List every debt: bond, credit cards, personal loans, retail accounts, SARS.",
          "Cancel debit orders on the deceased's accounts — but ONLY through the executor.",
        ],
        questions: [
          "What happens to a joint bank account when one holder dies?",
          "If the estate has no money, do the family owe the debts?",
        ],
      },
      {
        title: "Finalising and moving forward",
        body:
          "Estate finalisation typically takes 6–24 months. The executor advertises for creditors (they have 30 days to lodge claims), files the Liquidation & Distribution account, pays estate duty if applicable (20% above R3.5m), and finally distributes to beneficiaries.",
        checklist: [
          "Ask the executor for a monthly progress update.",
          "Once distributed, update YOUR own Will and beneficiary nominations to reflect the new picture.",
          "Speak to a bereavement counsellor if you need to — it's not a financial task, but it matters.",
        ],
        questions: [
          "What is estate duty and when does it apply?",
          "How do I lodge a complaint if the executor is dragging their feet?",
        ],
      },
    ],
  },

  business: {
    intro:
      "Registering the business is the easy part. The hard parts — SARS, banking, insurance, the right structure — trip up most first-time founders in SA. Here's the honest sequence.",
    steps: [
      {
        title: "Choose the right entity",
        body:
          "Most SA businesses should be a Pty Ltd (Proprietary Limited) — cheap to register (R125 via CIPC), limited-liability protection, and the standard structure lenders expect. A sole prop is simpler but exposes your personal assets. Don't create a CC (Close Corp) — no new ones can be registered.",
        checklist: [
          "Choose Pty Ltd unless there's a specific reason otherwise.",
          "Reserve the name via CIPC (R50) — check trademark clashes first.",
          "Appoint at least one director (usually you, at first).",
        ],
        questions: [
          "Pty Ltd vs sole prop — what's the real difference for tax?",
          "Do I need a shareholders agreement if I have a co-founder?",
        ],
      },
      {
        title: "Register with SARS — before your first invoice",
        body:
          "A Pty Ltd gets an income tax number automatically. You register for VAT only once turnover exceeds R1m/year (or voluntarily above R50k). Register for PAYE the moment you have your first employee (or you as director drawing a salary).",
        checklist: [
          "Confirm your Company Income Tax number is active on eFiling.",
          "Register for VAT if you're near or expect to cross R1m turnover.",
          "Register for UIF and Skills Development Levy (SDL) as soon as you have employees.",
        ],
        questions: [
          "When must I register for VAT and what does it mean for my pricing?",
          "What's the difference between provisional tax and PAYE?",
        ],
      },
      {
        title: "Open a business bank account",
        body:
          "You cannot mix personal and business banking legally (well, you can — but SARS will disallow expenses and you're piercing your own limited-liability veil). Every major bank offers a business account; fees vary 3–5x between them. Compare monthly account fees, transaction costs, and cash handling.",
        checklist: [
          "Compare at least three business account offers on monthly fee and transaction cost.",
          "Ensure the account supports EFT, debit orders, and a payment gateway.",
          "Keep every business expense receipt — bank statements alone aren't enough for SARS.",
        ],
        questions: [
          "What documents does a bank need to open a business account?",
          "Can I use a personal account for a side business?",
        ],
      },
      {
        title: "Get insured — before you sign your first client",
        body:
          "Professional Indemnity (PI) covers you if a client sues over your advice or work. Public Liability covers you if someone is injured at your premises. Business interruption covers loss of income if you can't trade. Missing PI can end a business on a single lawsuit.",
        checklist: [
          "Match insurance to actual risk: consultants → PI; retail → public liability; manufacturers → product liability.",
          "Get at least three quotes — SA business insurance is competitive.",
          "Read the exclusions — 'gross negligence', 'known circumstances', and 'sub-contractors' are the ones that catch founders out.",
        ],
        questions: [
          "Do I need PI cover as a small consultancy?",
          "What does 'business interruption' actually cover?",
        ],
      },
      {
        title: "Bookkeeping — from day one",
        body:
          "Every rand in and out must be captured. Use accounting software (Xero, Sage, QuickBooks) from month one — retrofitting a year of records is painful. Reconcile bank statements monthly. A bookkeeper costs R1–3k/month; an audit-fail costs a lot more.",
        checklist: [
          "Set up cloud accounting software from your first month.",
          "Reconcile your bank account every month — not once a year.",
          "Save every receipt (or a photo of it) — SARS requires 5 years of records.",
        ],
        questions: [
          "Do I need an accountant if I already have accounting software?",
          "What can a Pty Ltd deduct that a sole prop cannot?",
        ],
      },
      {
        title: "Retirement and personal cover as a founder",
        body:
          "Founders skip their own retirement contributions and life cover — then discover a decade later they've saved nothing. Contribute to an RA in your own name (deductible up to 27.5% of income); take enough life cover to protect the business AND your family.",
        checklist: [
          "Open (or continue) an RA — the tax deduction alone often pays for itself.",
          "Take life cover equal to at least outstanding business debt + 5 years of your income.",
          "If there's a co-founder, consider buy-and-sell insurance.",
        ],
        questions: [
          "How do RA contributions work when I pay myself dividends, not a salary?",
          "What's buy-and-sell insurance and do we need it?",
        ],
      },
    ],
  },

  education: {
    intro:
      "Education inflation runs 8–10% a year in SA — well above CPI. The good news: starting early beats starting big. Here's what actually works.",
    steps: [
      {
        title: "Work out the number",
        body:
          "A three-year undergraduate degree in SA today costs roughly R150–300k in tuition, plus R60–100k/year for residence + textbooks + living. Multiply by education inflation compounded to the year the child starts (usually 18 years out) — that's typically R1.5–3m today's terms.",
        checklist: [
          "Choose a target (undergrad in SA, undergrad overseas, postgrad).",
          "Estimate today's cost in Rands.",
          "Multiply by (1.08)^years_to_start — that's your future-value target.",
        ],
        questions: [
          "What's education inflation and where do I check it?",
          "Should I plan for SA or overseas study?",
        ],
      },
      {
        title: "Pick the right vehicle",
        body:
          "The three main options for education savings: Tax-Free Savings Account (TFSA), Unit Trust, and Endowment. TFSA is almost always the best for long horizons — completely tax-free growth. Endowments only make sense at very high tax brackets.",
        checklist: [
          "Open a TFSA — R36k/year contribution cap, R500k lifetime cap (as of 2024).",
          "Use unit trusts for anything above the TFSA cap.",
          "Avoid education-branded 'plans' unless you've read the TER and fees vs a plain-vanilla fund.",
        ],
        questions: [
          "TFSA vs endowment for a 10-year horizon — which wins?",
          "Can I open a TFSA in my child's name?",
        ],
      },
      {
        title: "Choose the underlying investment",
        body:
          "With a 15–18 year horizon you can afford equity exposure. A low-cost equity index tracker (Total Expense Ratio under 0.5%) is the default choice. Reduce equity as you get within 3–5 years of needing the money.",
        checklist: [
          "Pick a fund with a TER under 0.5% (0.2–0.3% is achievable).",
          "Aim for 70–100% equity in the early years.",
          "De-risk gradually starting 3–5 years before the child needs the money.",
        ],
        questions: [
          "What's a Total Expense Ratio and how do I compare funds?",
          "Should I include offshore exposure and how much?",
        ],
      },
      {
        title: "Automate it — the boring part is the powerful part",
        body:
          "R1 000/month invested from birth in a broad equity tracker (7% real return) compounds to roughly R500k in today's terms by age 18. Missing one month barely matters; skipping a decade does. Set up a debit order and never touch it.",
        checklist: [
          "Set up a debit order on the day salary arrives (not the day before it runs out).",
          "Increase the contribution by 6–8% each year with inflation.",
          "Review annually — do not check the balance daily.",
        ],
        questions: [
          "How much does R500/month become in 18 years?",
          "What if I can only start with R200/month?",
        ],
      },
      {
        title: "NSFAS, bursaries and Plan B",
        body:
          "If your household income is under R350k/year, NSFAS covers full undergraduate cost at a public university. Bursaries from corporates and foundations often cover the middle-income gap. Plan for the savings pot to be the top-up, not the whole thing.",
        checklist: [
          "Check NSFAS eligibility around Grade 10.",
          "Look at corporate bursaries — most open applications in the child's Grade 11 year.",
          "Have a Plan B (student loan, part-time work, gap year) — it's not failure, it's realism.",
        ],
        questions: [
          "Who qualifies for NSFAS?",
          "Are corporate bursaries worth applying for?",
        ],
      },
    ],
  },
};
